import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useSearchParams, Link } from 'react-router-dom'
import { Plus, Edit2, Trash2, Search, Users, AlertCircle } from 'lucide-react'
import { apiJson, type Page } from '../api'
import { canDeleteEmployees, canManageEmployees } from '../auth'
import Pager from '../components/Pager'
import SortableTh from '../components/SortableTh'

interface Employee {
  id: string
  employeeCode: string
  firstName: string
  lastName: string
  email: string
  phone?: string
  employmentStatus: string
  joinDate: string
  orgUnitId?: string
}

interface OrgUnit {
  id: string
  name: string
}

const EMPLOYMENT_STATUSES = ['applicant', 'hired', 'active', 'promoted', 'transferred', 'exited']

const statusMeta: Record<string, { label: string; cls: string; dot: string }> = {
  active: { label: 'Active', cls: 'bg-emerald-50 text-emerald-700 ring-emerald-100', dot: 'bg-emerald-500' },
  hired: { label: 'Hired', cls: 'bg-teal-50 text-teal-700 ring-teal-100', dot: 'bg-teal-500' },
  promoted: { label: 'Promoted', cls: 'bg-blue-50 text-blue-700 ring-blue-100', dot: 'bg-blue-500' },
  transferred: { label: 'Transferred', cls: 'bg-sky-50 text-sky-700 ring-sky-100', dot: 'bg-sky-500' },
  applicant: { label: 'Applicant', cls: 'bg-amber-50 text-amber-700 ring-amber-100', dot: 'bg-amber-500' },
  exited: { label: 'Exited', cls: 'bg-slate-100 text-slate-500 ring-slate-200', dot: 'bg-slate-400' },
}

function statusOf(s: string) {
  return statusMeta[s] ?? { label: s, cls: 'bg-slate-100 text-slate-500 ring-slate-200', dot: 'bg-slate-400' }
}

const avatarTints = [
  'from-blue-500 to-brand-600',
  'from-brand-600 to-brand-700',
  'from-emerald-500 to-teal-600',
  'from-amber-500 to-orange-600',
  'from-rose-500 to-pink-600',
  'from-cyan-500 to-sky-600',
]

function avatarTint(name: string) {
  let hash = 0
  for (const ch of name) hash = (hash * 31 + ch.charCodeAt(0)) | 0
  return avatarTints[Math.abs(hash) % avatarTints.length]
}

export default function Employees() {
  const navigate = useNavigate()
  const [page_data, setPageData] = useState<Page<Employee> | null>(null)
  const [orgUnits, setOrgUnits] = useState<OrgUnit[]>([])
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [loadError, setLoadError] = useState('')
  const [actionError, setActionError] = useState('')
  const [deletingId, setDeletingId] = useState<string | null>(null)

  // list controls (server-side)
  const [searchParams] = useSearchParams()
  const [query, setQuery] = useState(searchParams.get('q') ?? '')
  const [q, setQ] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [deptFilter, setDeptFilter] = useState('')
  const [page, setPage] = useState(1)
  const [size, setSize] = useState(20)
  const [sort, setSort] = useState('createdAt')
  const [order, setOrder] = useState<'ASC' | 'DESC'>('DESC')

  const allowManage = canManageEmployees()
  const allowDelete = canDeleteEmployees()

  // debounce the search box -> q (resets to page 1)
  useEffect(() => {
    const t = setTimeout(() => {
      setQ(query.trim())
      setPage(1)
    }, 300)
    return () => clearTimeout(t)
  }, [query])

  const handleSort = (field: string) => {
    if (sort === field) {
      setOrder(order === 'ASC' ? 'DESC' : 'ASC')
    } else {
      setSort(field)
      setOrder('ASC')
    }
    setPage(1)
  }

  const orgName = useCallback(
    (id?: string) => orgUnits.find((u) => u.id === id)?.name ?? '—',
    [orgUnits],
  )

  // org units load once (for department names + filters + form select)
  useEffect(() => {
    apiJson<OrgUnit[]>('/org-units')
      .then(setOrgUnits)
      .catch(() => {})
  }, [])

  const fetchEmployees = useCallback(async () => {
    setRefreshing(true)
    try {
      const params = new URLSearchParams({ page: String(page), size: String(size), sort, order })
      if (q) params.set('q', q)
      if (statusFilter) params.set('status', statusFilter)
      if (deptFilter) params.set('orgUnitId', deptFilter)
      const data = await apiJson<Page<Employee>>(`/employees?${params}`)
      setPageData(data)
      setLoadError('')
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : 'Failed to load employees')
    } finally {
      setLoading(false)
      setRefreshing(false)
    }
  }, [page, size, sort, order, q, statusFilter, deptFilter])

  useEffect(() => {
    fetchEmployees()
  }, [fetchEmployees])

  const employees = useMemo(() => page_data?.data ?? [], [page_data])

  const remove = async (emp: Employee) => {
    if (!window.confirm(`Delete ${emp.firstName} ${emp.lastName} (${emp.employeeCode})? This cannot be undone.`)) return
    setDeletingId(emp.id)
    setActionError('')
    try {
      await apiJson(`/employees/${emp.id}`, { method: 'DELETE' })
      await fetchEmployees()
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Delete failed')
    } finally {
      setDeletingId(null)
    }
  }

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-slate-200 border-t-brand-600" />
      </div>
    )
  }

  return (
    <div className="space-y-5">
      {/* Toolbar */}
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <div className="relative">
            <Search size={15} className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search people…"
              className="w-full rounded-xl border border-slate-200 bg-white py-2.5 pl-10 pr-4 text-sm outline-none transition-all placeholder:text-slate-400 focus:border-brand-400 focus:ring-4 focus:ring-brand-100 sm:w-64"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); setPage(1) }}
            className="rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-600 outline-none transition-all focus:border-brand-400 focus:ring-4 focus:ring-brand-100"
          >
            <option value="">All statuses</option>
            {EMPLOYMENT_STATUSES.map((s) => (
              <option key={s} value={s}>{statusOf(s).label}</option>
            ))}
          </select>
          <select
            value={deptFilter}
            onChange={(e) => { setDeptFilter(e.target.value); setPage(1) }}
            className="rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-600 outline-none transition-all focus:border-brand-400 focus:ring-4 focus:ring-brand-100"
          >
            <option value="">All departments</option>
            {orgUnits.map((u) => (
              <option key={u.id} value={u.id}>{u.name}</option>
            ))}
          </select>
        </div>
        {allowManage ? (
          <button
            onClick={() => navigate('/employees/new')}
            className="flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-brand-600 to-brand-700 px-4 py-2.5 text-sm font-semibold text-white shadow-lg shadow-brand-500/25 transition-all hover:shadow-xl hover:brightness-110"
          >
            <Plus size={16} />
            Add Employee
          </button>
        ) : (
          <span className="text-xs text-slate-400">You have read-only access</span>
        )}
      </div>

      {(loadError || actionError) && (
        <div className="flex items-center gap-2.5 rounded-xl border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-700">
          <AlertCircle size={16} className="shrink-0" />
          {loadError || actionError}
        </div>
      )}

      {/* Table */}
      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className={`overflow-x-auto transition-opacity ${refreshing ? 'opacity-60' : ''}`}>
          <table className="w-full min-w-[760px]">
            <thead>
              <tr className="border-b border-slate-100 bg-slate-50/60">
                <SortableTh label="Employee" field="firstName" sort={sort} order={order} onSort={handleSort} />
                <SortableTh label="Email" field="email" sort={sort} order={order} onSort={handleSort} />
                <th className="px-5 py-3.5 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-400">Department</th>
                <SortableTh label="Joined" field="joinDate" sort={sort} order={order} onSort={handleSort} />
                <SortableTh label="Status" field="employmentStatus" sort={sort} order={order} onSort={handleSort} />
                {(allowManage || allowDelete) && (
                  <th className="px-5 py-3.5 text-right text-[11px] font-semibold uppercase tracking-wider text-slate-400">Actions</th>
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {employees.map((emp) => {
                const fullName = `${emp.firstName} ${emp.lastName}`
                const initials = `${emp.firstName[0] ?? ''}${emp.lastName[0] ?? ''}`.toUpperCase()
                const st = statusOf(emp.employmentStatus)
                return (
                  <tr key={emp.id} className="group transition-colors hover:bg-slate-50/70">
                    <td className="px-5 py-3.5">
                      <Link to={`/employees/${emp.id}`} className="flex items-center gap-3 rounded-lg outline-none transition-colors hover:text-brand-700 focus-visible:ring-2 focus-visible:ring-brand-300">
                        <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-gradient-to-br text-xs font-bold text-white ${avatarTint(fullName)}`}>
                          {initials}
                        </div>
                        <div className="min-w-0">
                          <p className="truncate text-sm font-semibold underline-offset-2 group-hover:underline">{fullName}</p>
                          <p className="truncate text-xs text-slate-400">{emp.employeeCode}</p>
                        </div>
                      </Link>
                    </td>
                    <td className="px-5 py-3.5 text-sm text-slate-500">{emp.email}</td>
                    <td className="px-5 py-3.5">
                      <span className="rounded-lg bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-600">
                        {orgName(emp.orgUnitId)}
                      </span>
                    </td>
                    <td className="px-5 py-3.5 text-sm text-slate-500">{(emp.joinDate ?? '').slice(0, 10)}</td>
                    <td className="px-5 py-3.5">
                      <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium ring-1 ${st.cls}`}>
                        <span className={`h-1.5 w-1.5 rounded-full ${st.dot}`} />
                        {st.label}
                      </span>
                    </td>
                    {(allowManage || allowDelete) && (
                      <td className="px-5 py-3.5 text-right">
                        <div className="inline-flex gap-1 opacity-0 transition-opacity group-hover:opacity-100">
                          {allowManage && (
                            <button
                              onClick={() => navigate(`/employees/${emp.id}/edit`)}
                              className="rounded-lg p-2 text-slate-400 transition-colors hover:bg-brand-50 hover:text-brand-600"
                              title="Edit"
                            >
                              <Edit2 size={15} />
                            </button>
                          )}
                          {allowDelete && (
                            <button
                              onClick={() => remove(emp)}
                              disabled={deletingId === emp.id}
                              className="rounded-lg p-2 text-slate-400 transition-colors hover:bg-red-50 hover:text-red-600 disabled:opacity-40"
                              title="Delete (admin only)"
                            >
                              <Trash2 size={15} />
                            </button>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>

        {employees.length === 0 && !loadError && (
          <div className="flex flex-col items-center py-14 text-center">
            <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-slate-100 text-slate-400">
              <Users size={22} />
            </div>
            <p className="mt-3 text-sm font-semibold">No employees found</p>
            <p className="mt-1 text-xs text-slate-400">
              {query ? `Nothing matches “${query}”.` : 'Add your first employee to get started.'}
            </p>
          </div>
        )}

        <Pager
          page={page}
          totalPages={page_data?.totalPages ?? 1}
          total={page_data?.total ?? 0}
          size={size}
          onPage={setPage}
          onSize={(s) => { setSize(s); setPage(1) }}
        />
      </div>
    </div>
  )
}
